/** Small node-building helpers shared by the layers, ambience and cue player. */
import type { SoundKit } from './kit';

/** An AudioParam driven by smoothed targets; skips redundant automation calls. */
export class Smooth {
  private last = Number.NaN;
  constructor(private ctx: BaseAudioContext, readonly param: AudioParam, private tau = .08, private eps = 1e-4, private rel = .004) {}
  set(value: number, tau = this.tau) {
    if (!Number.isFinite(value)) return;
    if (Math.abs(value - this.last) <= this.eps + Math.abs(value) * this.rel) return;
    this.last = value;
    this.param.setTargetAtTime(value, this.ctx.currentTime, tau);
  }
  get target() { return this.last; }
}

export type Target = AudioNode | AudioParam;
/**
 * Control-rate parameters: a biquad with sample-accurate automation recomputes
 * its coefficients every sample, which is the single biggest CPU cost on phones.
 */
export function kRate(...params: AudioParam[]) {
  for (const p of params) { try { if ('automationRate' in p) p.automationRate = 'k-rate'; } catch { /* fixed-rate param */ } }
}
const isParam = (t: Target): t is AudioParam => typeof (t as AudioParam).setTargetAtTime === 'function' && !('connect' in t);
/** Connect a node to another node or to an AudioParam (modulation). */
export function link(from: AudioNode, to: Target) { if (isParam(to)) from.connect(to); else from.connect(to); }

export class Graph {
  readonly sources: AudioScheduledSourceNode[] = [];
  constructor(readonly ctx: BaseAudioContext, readonly kit: SoundKit) {}
  gain(value = 0, ...to: Target[]): GainNode {
    const g = this.ctx.createGain(); g.gain.value = value;
    for (const node of to) link(g, node);
    return g;
  }
  filter(type: BiquadFilterType, frequency: number, q = .7, ...to: Target[]): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter(); f.type = type; f.frequency.value = frequency; f.Q.value = q;
    kRate(f.frequency, f.Q, f.detune);
    for (const node of to) link(f, node);
    return f;
  }
  /** A looping buffer source started at a random offset (decorrelates shared buffers). */
  loop(buffer: AudioBuffer, rate = 1, ...to: Target[]): AudioBufferSourceNode {
    const s = this.ctx.createBufferSource(); s.buffer = buffer; s.loop = true; s.playbackRate.value = rate;
    for (const node of to) link(s, node);
    s.start(this.ctx.currentTime, Math.random() * buffer.duration);
    this.sources.push(s);
    return s;
  }
  osc(wave: PeriodicWave | OscillatorType, frequency: number, ...to: Target[]): OscillatorNode {
    const o = this.ctx.createOscillator();
    if (typeof wave === 'string') o.type = wave as OscillatorType; else o.setPeriodicWave(wave as PeriodicWave);
    o.frequency.value = frequency;
    kRate(o.detune);
    for (const node of to) link(o, node);
    o.start(this.ctx.currentTime);
    this.sources.push(o);
    return o;
  }
  /** Gain whose value is `base + depth × modulation(t)`; returns the node and its depth control. */
  modulated(base: number, depth: number, modulation: AudioBuffer, rate = 1): { node: GainNode; depth: GainNode } {
    const node = this.gain(base), d = this.gain(depth, node.gain);
    this.loop(modulation, rate, d);
    return { node, depth: d };
  }
  /** Stereo pair from two decorrelated mono chains, with partial cross-feed. */
  stereo(left: AudioNode, right: AudioNode, crossfeed: number, ...to: Target[]): ChannelMergerNode {
    const m = this.ctx.createChannelMerger(2);
    left.connect(m, 0, 0); right.connect(m, 0, 1);
    if (crossfeed > 0) { left.connect(this.gain(crossfeed)).connect(m, 0, 1); right.connect(this.gain(crossfeed)).connect(m, 0, 0); }
    for (const node of to) link(m, node);
    return m;
  }
  stop(at: number) {
    for (const s of this.sources) { try { s.stop(at); } catch { /* already stopped */ } }
  }
}
